import React from 'react';
import { PageId } from '../types';
import { 
  Home, 
  ShoppingBag, 
  TrendingUp, 
  FileText, 
  Sparkles,
  BookOpen,
  MessageSquare,
  Tag,
  PenTool,
  Truck,
  Target,
  Layers,
  LogOut,
  BarChart3,
  ClipboardList
} from 'lucide-react';

interface SidebarProps {
  activePage: PageId;
  setActivePage: (page: PageId) => void;
  allowedModules: string[];
  userEmail: string;
  onLogout: () => void;
}

interface NavItem {
  id: PageId;
  label: string;
  icon: React.ComponentType<any>;
  subtitle: string;
}

interface NavCategory {
  title: string;
  items: NavItem[];
}

export default function Sidebar({ activePage, setActivePage, allowedModules, userEmail, onLogout }: SidebarProps) {
  const categories: NavCategory[] = [
    {
      title: 'General',
      items: [
        { id: 'overview', label: 'Overview', icon: Home, subtitle: 'Unified Hub' },
        { id: 'scorecard', label: 'Scorecard', icon: ClipboardList, subtitle: 'Weekly / Monthly / Quarterly' },
        { id: 'dtc-kpis', label: 'DTC KPIs', icon: BarChart3, subtitle: 'Blended Ads + GA4' }
      ]
    },
    {
      title: 'Revenue Ops',
      items: [
        { id: 'meta-ads', label: 'Meta Ads', icon: TrendingUp, subtitle: 'Paid Social & ROAS' },
        { id: 'shopify', label: 'Shopify DTC', icon: ShoppingBag, subtitle: 'Storefront sales' },
        { id: 'google-ads', label: 'Google Ads', icon: Sparkles, subtitle: 'Search, Shopping, SEM' }
      ]
    },
    {
      title: 'B2B',
      items: [
        { id: 'hubspot', label: 'HubSpot', icon: Target, subtitle: 'Corporate gifting & pipelines' }
      ]
    },
    {
      title: 'Design',
      items: [
        { id: 'design-tracker', label: 'Design Tracker', icon: PenTool, subtitle: 'Google Sheet sync' }
      ]
    },
    {
      title: 'Merchandising',
      items: [
        { id: 'shopify-merch', label: 'Shopify Merch KPIs', icon: Layers, subtitle: 'SKU velocities' },
        { id: 'category-ops', label: 'Category Ops Sheet', icon: FileText, subtitle: 'Margins & COGS matrix' }
      ]
    },
    {
      title: 'Warehouse',
      items: [
        { id: 'unicommerce', label: 'Unicommerce', icon: Truck, subtitle: 'Logistics & SLA splits' }
      ]
    },
    {
      title: 'Finance',
      items: [
        { id: 'zoho', label: 'Zoho Books', icon: BookOpen, subtitle: 'B2B & wholesale invoice' }
      ]
    },
    {
      title: 'CX',
      items: [
        { id: 'bitespeed', label: 'Bitespeed', icon: MessageSquare, subtitle: 'WhatsApp CSAT & chat' },
        { id: 'shopify-tags', label: 'Shopify Tags', icon: Tag, subtitle: 'VIP & customer segments' }
      ]
    }
  ];

  const visibleCategories = categories
    .map((category) => ({
      ...category,
      items: category.items.filter((item) => allowedModules.includes(item.id)),
    }))
    .filter((category) => category.items.length > 0);

  return (
    <aside 
      id="sidebar" 
      className="w-72 bg-[#f8fafc]/90 backdrop-blur-md border-r border-slate-200/60 flex flex-col justify-between shrink-0"
    >
      <div className="flex-1 overflow-y-auto p-5 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent space-y-6">
        {/* Header Branding */}
        <div className="p-1 mb-2">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 bg-slate-50 border border-slate-200/80 rounded-lg flex items-center justify-center font-bold text-lg text-black shadow-xs">N</div>
            <span className="text-xl font-bold tracking-tight uppercase font-arial text-slate-800">Nooe</span>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Intelligence Studio</span>
        </div>

        {/* Categories of Navigation */}
        <div className="space-y-5">
          {visibleCategories.map((category) => (
            <div key={category.title} className="space-y-1">
              <span className="px-3 text-[9.5px] font-mono uppercase tracking-widest text-slate-400 font-bold block mb-2">
                {category.title}
              </span>
              
              <div className="space-y-1">
                {category.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activePage === item.id;
                  return (
                    <button
                      id={`nav-${item.id}`}
                      data-testid={`nav-${item.id}`}
                      key={item.id}
                      onClick={() => setActivePage(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 text-left group border ${
                        isActive 
                          ? 'bg-white text-slate-800 border-slate-200 shadow-sm' 
                          : 'text-slate-500 hover:text-slate-800 border-transparent hover:bg-slate-200/40'
                      }`}
                    >
                      {/* Custom dot indicator matching Bento aesthetic */}
                      <div className={`w-1.5 h-1.5 rounded-full transition-all duration-300 shrink-0 ${
                        isActive 
                          ? 'bg-slate-800 shadow-[0_0_6px_rgba(30,41,59,0.3)]' 
                          : 'bg-slate-300 group-hover:bg-slate-400'
                      }`} />

                      <div className="min-w-0 flex-1">
                        <div className={`text-[11px] truncate font-medium ${isActive ? 'text-slate-800 font-semibold' : 'text-slate-600 group-hover:text-slate-800'}`}>
                          {item.label}
                        </div>
                        <div className={`text-[9px] truncate ${isActive ? 'text-slate-400' : 'text-slate-400 group-hover:text-slate-500'}`}>
                          {item.subtitle}
                        </div>
                      </div>
                      
                      <Icon className={`h-3.5 w-3.5 shrink-0 transition-opacity opacity-40 group-hover:opacity-80 ${isActive ? 'text-slate-800 opacity-100' : ''}`} />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer Segment */}
      <div className="p-4 border-t border-slate-100 space-y-3">
        <div className="p-3.5 rounded-2xl bg-white border border-slate-100 shadow-[0_4px_20px_rgba(143,160,185,0.05)] flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[9px] text-slate-400 uppercase tracking-widest mb-0.5 font-bold font-mono truncate" title={userEmail}>{userEmail}</p>
            <p className="text-[11px] text-emerald-600 flex items-center gap-2 font-medium">
              <span className="block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              Real-time Sync Active
            </p>
          </div>
          <button
            id="sidebar-logout-btn"
            data-testid="sidebar-logout-button"
            onClick={onLogout}
            title="Sign out"
            className="p-2 rounded-xl border border-slate-200/60 text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-all shrink-0"
          >
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </aside>
  );
}
